import { useEffect, useState } from 'react';
import { useClientState } from '@state/useClientState';
import { useStoreOutlet } from '@state/useStoreOutlet';
import { useMiniappReq } from '@state/useMiniappReq';
import { useTokenCredentials } from '@state/useTokenCredentials';
import useClientAmplify from './useClientAmplify';
import { getDB } from '@/InitiateRxdb';
import { useStoreSettings } from '@state/useStoreSettings';
import { useStoreViewBill } from '@state/useStoreViewBill';
import { useLambdaFunctionContext } from '@context/LambdaFunctionContext';
import { ObjectId } from 'bson';

export default function useRxdbMiniappReq() {
  const [key, setKey] = useState('');
  const [clientAmplify] = useClientAmplify();

  const { getClient, updateClient } = useClientState((state) => ({
    getClient: state.getClient,
    updateClient: state.updateClient,
  }));

  const { getOutletInfo, getOutletId } = useStoreOutlet((state) => ({
    getOutletInfo: state.getOutletInfo,
    getOutletId: state.getOutletId,
  }));

  const { getTokenCredentials } = useTokenCredentials((state) => ({
    getTokenCredentials: state.getTokenCredentials,
  }));

  const { updateMiniappReq, getMiniappReq, getMiniappReqId, updateMiniappReqXendit, updateMiniappReqMessage } = useMiniappReq((state) => ({
    updateMiniappReq: state.updateMiniappReq,
    getMiniappReq: state.getMiniappReq,
    getMiniappReqId: state.getMiniappReqId,
    updateMiniappReqXendit: state.updateMiniappReqXendit,
    updateMiniappReqMessage: state.updateMiniappReqMessage,
  }));

  const { getSetting } = useStoreSettings((state) => ({
    getSetting: state.getSetting,
  }));

  const { viewBillId: transId } = useStoreViewBill((state => ({
    viewBillId: state.viewBillId,
  })))

  const { checkTransactionStatus } = useLambdaFunctionContext();

  const subscribe = async () => {
    setKey('transaction');
    updateMiniappReqMessage(null);
    const now = new Date();
    let timeString = now.toLocaleTimeString(getOutletInfo().localeCode, {
      hour12: false,
    });
    console.log('Subscribe confirm miniapp request called at:', timeString);
    console.log('Timeout limit', window.TIMEOUT);

    const timeout = setTimeout(() => {
      console.log('Timeout!', window.TIMEOUT);
      updateMiniappReq('not_updated');
    }, window.TIMEOUT || 20000);

    //standby
    const db = await getDB();

    if (!db || !db.miniapprequests) {
      console.error('Database or miniapprequests collection not available');
      updateMiniappReq('not_updated');
      clearTimeout(timeout);
      setKey('');
      return;
    }

    const obj = {
      partition: getOutletId(), // Partition, a.k.a store_uuid, a.k.a outlet_id
      id: getMiniappReqId(),
    };
    const query = db.miniapprequests
      .findOne({
        selector: obj,
      })
      .sort({ _rev: 'desc' });

    const querySub = await query.$.subscribe((doc) => {
      console.log('storelog confirm subscribe received at:', timeString);

      // "new" is not an answer: fe-lambda creates this very row as status "new" when it
      // accepts the order (clientTransaction, under the id this page generated and sent),
      // and the miniapp patches it to a verdict afterwards. The browser is fed a live
      // stream of pushed documents (stream$ in InitiateRxdb), so the row's initial state
      // can arrive first — and taking it as the verdict reported a created order as
      // failed, because the callers treat anything that is not "updated" as a failure.
      // A quick order would be sitting in the cashier's pull list while the customer was
      // told it failed and never saw their QR.
      //
      // Only this subscription needs the guard. subscribeViewBill filters for a terminal
      // status in its selector, and the Xendit and Snap subscriptions wait for "new" on
      // purpose — those are transaction_payment rows, a different exchange — so do not
      // "harmonise" them with this.
      if (!doc || doc.status === 'new') return;

      updateMiniappReq(doc.status);
      if (doc.status !== 'updated') {
        try {
          const meta = JSON.parse(doc.meta ?? '{}');
          if (meta.message) updateMiniappReqMessage(meta.message);
        } catch {
          // ignore parse errors, keep message null
        }
      }
      clearTimeout(timeout);
      setKey('');
    });

    // stop watching this query
    return () => {
      querySub.unsubscribe();
    };
  };

  const subscribeViewBill = async () => {
    setKey('update_view_bill');
    const timeout = setTimeout(() => {
      updateMiniappReq('not_updated');
    }, 10000);
    //standby
    const db = await getDB();

    if (!db || !db.miniapprequests) {
      console.error('Database or miniapprequests collection not available');
      updateMiniappReq('not_updated');
      clearTimeout(timeout);
      setKey('');
      return;
    }

    const orSelectors = [{ id: getMiniappReqId() }];
    if (transId) {
      orSelectors.push({ key: 'transaction', value: transId });
    }

    const query = db.miniapprequests
      .findOne({
        selector: {
          status: 'updated',
          partition: getOutletId(),
          $or: orSelectors,
        },
      })
      .sort({ _rev: 'desc' });

    const querySub = await query.$.subscribe((doc) => {
      console.log(doc);

      if (doc && doc.status === 'updated') {
        updateMiniappReq(doc.status);
        clearTimeout(timeout);
        setKey('');
        return;
      }
    });

    // stop watching this query
    return () => {
      querySub.unsubscribe();
    };
  };

  const subscribeMiniappRequestXendit = async () => {
    const timeString = new Date().toLocaleTimeString(getOutletInfo().localeCode, {
      hour12: false,
    });
    console.log('Subscribe miniapp request xendit called at:', timeString);

    let settled = false;
    let querySub;
    let poll;
    let timeout;
    const stop = () => {
      settled = true;
      clearTimeout(timeout);
      clearInterval(poll);
      querySub?.unsubscribe();
    };
    const settle = (status) => {
      if (settled) return;
      stop();
      updateMiniappReqXendit(status);
    };

    timeout = setTimeout(() => {
      console.log('Xendit payment timeout after 20 seconds');
      settle('timeout');
    }, 20000);

    const db = await getDB();

    if (!db || !db.miniapprequests) {
      console.error('Database or miniapprequests collection not available');
      settle('timeout');
      return;
    }

    // meta is left out of the selector and checked below: RxDB compares it as an exact
    // string, and the backend's meta also carries payment_status, so it never matched.
    const query = db.miniapprequests
      .find({
        selector: {
          status: 'new',
          partition: getOutletId(),
          key: 'transaction_payment',
          value: transId,
        },
      })
      .sort({ _rev: 'desc' });

    querySub = query.$.subscribe((docs) => {
      let failed = false;
      for (const doc of docs ?? []) {
        let meta;
        try {
          meta = JSON.parse(doc.meta ?? '{}');
        } catch {
          continue;
        }
        if (meta.gate !== 'xendit') continue;
        if (meta.payment_status === 'EXPIRED' || meta.payment_status === 'FAILED') {
          failed = true;
          continue;
        }
        console.log('Xendit payment confirmed via RxDB subscription', { trans_id: transId, timeString });
        settle('success');
        return;
      }
      if (failed) settle('timeout');
    });
    if (settled) querySub.unsubscribe();

    // The miniapp closes the transaction once the POS has taken the payment; keep
    // checking for that too, since the signal may arrive after the customer returns.
    const checkClosed = async () => {
      try {
        const checkStatus = await checkTransactionStatus({
          transid: new ObjectId(transId),
          _partition: getOutletId(),
        });
        if (checkStatus?.status === 'close') {
          console.log('Xendit payment confirmed via status poll', { trans_id: transId });
          settle('success');
        }
      } catch (error) {
        console.error('checkTransactionStatus failed:', error);
      }
    };
    await checkClosed();
    if (!settled) poll = setInterval(checkClosed, 3000);

    return stop;
  };

  useEffect(() => {
    if (
      Object.keys(getClient()).length > 0 &&
      key === 'transaction' &&
      getMiniappReq() !== 'updated' &&
      getSetting().orderMethod !== 'normal_order' &&
      getSetting().orderMethod !== 'waiter_order'
    ) {
      subscribe();
    }
  }, [getClient(), key === 'transaction', getMiniappReq() !== 'updated']);

  useEffect(() => {
    if (
      Object.keys(getClient()).length > 0 &&
      key === 'update_view_bill' &&
      getMiniappReq() !== 'updated'
    ) {
      subscribeViewBill();
    }
  }, [getClient(), key === 'update_view_bill', getMiniappReq() !== 'updated']);

  useEffect(() => {
    if (!getClient().graphql && getTokenCredentials() && getTokenCredentials().access_token) {
      const client = clientAmplify(getTokenCredentials().access_token);
      updateClient(client);
    }
  }, [getClient()]);

  const subscribeMiniappRequestSnap = async () => {
    const now = new Date();
    let timeString = now.toLocaleTimeString(getOutletInfo().localeCode, { hour12: false });
    console.log('Subscribe miniapp request SNAP called at:', timeString);

    // QR codes expire in 1hr — timeout a bit before that
    const timeout = setTimeout(() => {
      console.log('SNAP payment timeout after 55 minutes');
      updateMiniappReqXendit('timeout');
    }, 55 * 60 * 1000);

    const db = await getDB();

    if (!db || !db.miniapprequests) {
      console.error('Database or miniapprequests collection not available');
      updateMiniappReqXendit('timeout');
      clearTimeout(timeout);
      return;
    }

    // Watch for transaction_payment with gate="snap"
    // Don't include meta in the selector — RxDB does exact string match and the JSON key order
    // from the Realm function may vary. Check gate in the callback instead.
    const query = db.miniapprequests
      .find({
        selector: {
          status: 'new',
          partition: getOutletId(),
          key: 'transaction_payment',
          value: transId,
        },
      })
      .sort({ _rev: 'desc' })
      .limit(1);

    const querySub = await query.$.subscribe((docs) => {
      const doc = docs?.[0];
      if (!doc) return;
      try {
        const meta = JSON.parse(doc.meta ?? '{}');
        if (meta.gate === 'snap') {
          console.log('SNAP payment confirmed via RxDB subscription', { trans_id: transId });
          updateMiniappReqXendit('success');
          clearTimeout(timeout);
          querySub.unsubscribe();
        }
      } catch {
        // ignore parse errors
      }
    });

    // Fallback poll — check if transaction already closed
    const checkStatus = await checkTransactionStatus({
      transid: new ObjectId(transId),
      _partition: getOutletId(),
    });
    if (checkStatus.status === 'close') {
      console.log('SNAP payment confirmed via status poll', { trans_id: transId });
      updateMiniappReqXendit('success');
      querySub.unsubscribe();
      clearTimeout(timeout);
      return;
    }

    return () => {
      querySub.unsubscribe();
      clearTimeout(timeout);
    };
  };

  return [subscribe, subscribeViewBill, subscribeMiniappRequestXendit, subscribeMiniappRequestSnap];
}
